import React, { useCallback, useRef } from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import PageIcon from '@atlaskit/icon/core/page';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { chatHeaderTestIds } from '@atlassian/conversation-assistant-ui-components/constants';

import { SPLIT_LAYOUT_MIN_PANEL_WIDTH } from '../../../common/constants/private';
import { useSplitView } from '../useSplitView';

import messages from './messages';
import { DEFAULT_COLLAPSED_PANEL_WIDTH } from './utils/constants';
import { expandPanelToSplitLayout } from './utils/expandPanelToSplitLayout';
import { getCurrentPanelWidth } from './utils/getCurrentPanelWidth';
import { getExpandedPanelWidth } from './utils/getExpandedPanelWidth';
import { resizePanelTo } from './utils/resizePanelTo';

export type SplitLayoutHeaderButtonProps = {
	onToggle?: (isOpen: boolean) => void;
};

/**
 * Header toggle button that opens/closes the chat's split layout.
 *
 * Opening expands the panel the chat renders inside to its 50% cap and reveals the split-view right pane
 * Closing restores the panel to its previous (pre-expansion) width and hides the right pane.
 *
 * The open/closed state is shared with `SessionContextLayout` via {@link useSplitView} so the right pane's visibility stays in sync with the toggle.
 * Rendered immediately before the switch-mode icon in the chat header.
 */
export const SplitLayoutHeaderButton = ({
	onToggle,
}: SplitLayoutHeaderButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const buttonRef = useRef<HTMLButtonElement>(null);
	const preExpandWidthRef = useRef<number | null>(null);

	const { closeSessionContext, openSessionContext, presentation, rightPanelExitHandlerRef } =
		useSplitView();
	const isOpen = presentation !== null;

	const handleClick = useCallback(() => {
		if (!isOpen) {
			if (rightPanelExitHandlerRef) {
				rightPanelExitHandlerRef.current = null;
			}

			getExpandedPanelWidth(buttonRef.current, (expandedPanelWidth) => {
				const requestedPresentation =
					expandedPanelWidth !== null && expandedPanelWidth >= SPLIT_LAYOUT_MIN_PANEL_WIDTH
						? 'split'
						: 'drawer';
				const resolvedPresentation = openSessionContext(requestedPresentation);

				if (resolvedPresentation === 'split') {
					preExpandWidthRef.current = getCurrentPanelWidth(buttonRef.current);
					expandPanelToSplitLayout(buttonRef.current);
				}

				sendAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'viewContextButton',
					attributes: {
						isOpen: true,
						presentation: resolvedPresentation,
					},
				});

				onToggle?.(true);
			});
			return;
		}

		if (presentation === 'split') {
			// Only the split expands the panel, so only the split needs its width restored. Defer the
			// restore until the right pane has finished animating out (SplitLayout fires the exit handler).
			const button = buttonRef.current;
			const targetWidth = preExpandWidthRef.current ?? DEFAULT_COLLAPSED_PANEL_WIDTH;
			if (rightPanelExitHandlerRef) {
				rightPanelExitHandlerRef.current = () => resizePanelTo(button, targetWidth);
			} else {
				resizePanelTo(button, targetWidth);
			}
		}

		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'viewContextButton',
			attributes: {
				isOpen: false,
				presentation,
			},
		});

		closeSessionContext();
		onToggle?.(false);
	}, [
		closeSessionContext,
		isOpen,
		onToggle,
		openSessionContext,
		presentation,
		rightPanelExitHandlerRef,
		sendAnalyticsEvent,
	]);

	return (
		<IconButton
			ref={buttonRef}
			testId={chatHeaderTestIds.splitLayoutButton}
			appearance="subtle"
			isSelected={isOpen}
			isTooltipDisabled={false}
			label={
				isOpen
					? formatMessage(messages.splitLayoutCollapseButtonLabel)
					: formatMessage(messages.splitLayoutExpandButtonLabel)
			}
			icon={PageIcon}
			onClick={handleClick}
		/>
	);
};
